import React from 'react'

import { Column, ColumnEditorOptions } from 'primereact/column'
import { DataTableBaseProps, DataTableValueArray } from 'primereact/datatable'

import { ColumnConfig } from '../../../../features/ui-config'
import { getConfigTranslation } from '../../../helper/getConfigTranslation'
import { resolveValueByPath } from '../../../helper/resolveValueByPath'
import { DropDownOptions } from '../ConfigurableTable'
import { getBodyTemplate, getDataType, getFilterTemplate } from '../getter'

const createNavigationColumn = (header?: React.ReactNode) => (
  <Column
    key="navigationColumn"
    field="icon"
    header={header ?? ''}
    body={<i className="pi pi-angle-right text-primary text-2xl"></i>}
    frozen
    alignFrozen="right"
    align="right"
  />
)

export const navigationColumn = createNavigationColumn()

export function createColumns(
  configColumns: ColumnConfig[],
  props: DataTableBaseProps<DataTableValueArray>,
  checkboxen?: boolean,
  buttons?: JSX.Element,
  hasLink?: boolean,
  dropDownOptions?: DropDownOptions,
  children?: JSX.Element[],
  navigationColumnHeader?: React.ReactNode,
) {
  const checkboxColumn = checkboxen && (
    <Column
      key="checkboxColumn"
      selectionMode="multiple"
      frozen
      alignFrozen="left"
      className="w-3rem"
    />
  )

  const emptyColumn = <Column key="emptyColumn" field="" header="" />

  // Process child columns if provided
  let childColumns = children?.map((child: JSX.Element) => {
    const columnField = child.props.field

    // Find the corresponding config for the column
    const matchingConfig = configColumns.find(
      column =>
        column.field === columnField ||
        (column.filterField && column.filterField === columnField),
    )

    if (matchingConfig) {
      return React.cloneElement(child, {
        ...matchingConfig, // Merge config data
        key: child.props.field,
        header: getConfigTranslation(matchingConfig?.header), // Update header
        align: matchingConfig.align ?? child.props.align,
        // body:
        //   child.props.body ||
        //   ((rowData: unknown) =>
        //     getBodyTemplate(
        //       matchingConfig.dataType,
        //       resolveValueByPath(matchingConfig.field || '', rowData),
        //       rowData,
        //     )),
        // editor: child.props.editor ?? ((options: ColumnEditorOptions) => <></>),
        // filter: matchingConfig.filter ?? false,
        // filterElement: matchingConfig.filter
        //   ? getFilterTemplate(matchingConfig.dataType, dropDownOptions)
        //   : undefined,
        filterPlaceholder:
          child.props.filterPlaceholder ||
          getConfigTranslation(matchingConfig?.filterPlaceholder),
        style: {
          ...child.props.style,
          width: matchingConfig.width,
          minWidth: matchingConfig.minWidth,
        },
      })
    }

    // If no matching config, return the child as is
    return child
  })

  // Generate columns from configColumns if no children provided
  if (!childColumns || childColumns.length === 0) {
    childColumns = configColumns
      .filter(column => column.enabled !== false)
      .map(column => (
        <Column
          key={column.filterField || column.field}
          align={column.align ?? 'left'}
          alignFrozen={column.alignFrozen}
          body={(rowData: unknown) =>
            getBodyTemplate(
              column.dataType,
              resolveValueByPath(column.filterField || column.field, rowData),
              rowData,
            )
          }
          dataType={getDataType(column.dataType)}
          field={column.filterField ? undefined : column.field}
          filter={column.filter ?? false}
          filterField={column.filterField}
          filterElement={getFilterTemplate(column.dataType, dropDownOptions)}
          filterMenuStyle={{ minWidth: '16em' }}
          filterPlaceholder={getConfigTranslation(column?.filterPlaceholder)}
          frozen={column.frozen}
          header={getConfigTranslation(column?.header)}
          sortable={column.sortable ?? false}
          sortField={column.sortField}
          style={{ width: column.width, minWidth: column.minWidth }}
        />
      ))
  }

  if (checkboxColumn) {
    childColumns.unshift(checkboxColumn)
  }

  if (buttons) {
    childColumns.push(
      React.cloneElement(buttons, {
        frozen: true,
        align: 'right',
        alignFrozen: 'right',
      }),
    )
  }

  if (hasLink) {
    childColumns.push(createNavigationColumn(navigationColumnHeader))
  }

  // If a column has a width property there must be an empty column
  // at the end because otherwise PrimeReact tries to distribute
  // the column width equal to the table width so the last empty
  // column takes up the remaining space
  const hasWidthProperty = configColumns.some(column =>
    column.hasOwnProperty('width'),
  )

  if (hasWidthProperty && !(buttons || hasLink)) {
    childColumns.push(emptyColumn)
  }

  return childColumns
}
